import { motion } from "framer-motion";
import { ArrowDown, Mail, Phone, Star } from "lucide-react";
import { EMAIL, GOOGLE, PHONE, PHONE_HREF } from "@/components/Nav";

const lines = [
  { text: "YOUR JUNK.", cls: "text-white" },
  { text: "GONE TODAY.", cls: "text-royal" },
];

const lineVariants = {
  hidden: { y: "115%", rotate: 2 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.55 + i * 0.16 },
  }),
};

export default function Hero() {
  return (
    <section id="top" className="grain relative overflow-hidden bg-navy pt-24" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="relative px-5 pb-16 pt-10 sm:px-10 lg:pb-24 lg:pt-16 xl:px-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.45, duration: 0.6 }}
          className="mb-8 font-mono2 text-[11px] uppercase tracking-[0.35em] text-white/60 sm:text-xs"
          data-testid="hero-kicker"
        >
          Junk Removal — Massachusetts &amp; Rhode Island
        </motion.p>
        <h1 className="font-display uppercase leading-[0.92] text-[17vw] sm:text-[13vw] lg:text-[9rem] xl:text-[11rem]" data-testid="hero-headline">
          {lines.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block origin-left ${l.cls}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.05, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-md text-base text-white/75 md:text-lg"
          data-testid="hero-subline"
        >
          Run by a 20-year-old college athlete. Houses, garages, yards, offices —
          you point at it, we haul it, it's gone today.
        </motion.p>
        <motion.a
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          href={GOOGLE}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 flex w-fit items-center gap-3 transition-colors duration-200 hover:opacity-80"
          data-testid="hero-rating-badge"
        >
          <span className="flex gap-1">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star key={s} className="h-5 w-5 fill-[#FBBC04] text-[#FBBC04] sm:h-6 sm:w-6" strokeWidth={1.5} />
            ))}
          </span>
          <span className="font-display text-lg uppercase tracking-wide text-white sm:text-xl">
            5-Star Rated on Google
          </span>
        </motion.a>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <a
            href={PHONE_HREF}
            data-testid="hero-call-button"
            className="btn-hard btn-hard-light flex items-center justify-center gap-3 bg-royal px-8 py-5 font-display text-xl tracking-wide text-white"
          >
            <Phone className="h-5 w-5" strokeWidth={2.5} />
            CALL {PHONE}
          </a>
          <a
            href="#quote"
            data-testid="hero-quote-button"
            className="btn-hard btn-hard-light flex items-center justify-center gap-3 border-2 border-white/70 px-8 py-5 font-display text-xl tracking-wide text-white hover:bg-white hover:text-navy"
          >
            FREE QUOTE
            <ArrowDown className="h-5 w-5" strokeWidth={2.5} />
          </a>
        </motion.div>
        <motion.a
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.35, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          href={`mailto:${EMAIL}`}
          data-testid="hero-email-link"
          className="mt-6 inline-flex w-fit items-center gap-3 border-b-2 border-royal pb-1 text-base text-white transition-colors duration-200 hover:text-royal md:text-lg"
        >
          <Mail className="h-5 w-5 shrink-0 text-royal" strokeWidth={2.5} />
          <span className="font-mono2 tracking-wide">{EMAIL}</span>
        </motion.a>
      </div>
      <div className="relative flex items-center justify-between border-t border-white/15 px-5 py-4 sm:px-10 xl:px-16">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/50 sm:text-[11px]">
          Same-day &amp; next-day pickups
        </p>
        <p className="hidden font-mono2 text-[11px] uppercase tracking-[0.3em] text-white/50 sm:block">
          Scroll ↓
        </p>
      </div>
    </section>
  );
}
